import ExcelJS from "exceljs";

const MAX_DATA_ROWS = 1000;
const TEXT_FORMAT = "@";

function applyTextFormatToColumn(worksheet, columnIndex, startRow, endRow) {
  const column = worksheet.getColumn(columnIndex);
  column.numFmt = TEXT_FORMAT;
  for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
    const cell = worksheet.getCell(rowIndex, columnIndex);
    cell.numFmt = TEXT_FORMAT;
  }
}

function addListDropdown(workbook, worksheet, keys, columnIndex, hiddenSheetName) {
  if (!keys.length) {
    return;
  }

  const listSheet = workbook.addWorksheet(hiddenSheetName, { state: "veryHidden" });
  keys.forEach((key, index) => {
    listSheet.getCell(index + 1, 1).value = key;
  });

  const columnLetter = worksheet.getColumn(columnIndex).letter;
  const range = `${columnLetter}2:${columnLetter}${MAX_DATA_ROWS + 1}`;
  worksheet.dataValidations.add(range, {
    type: "list",
    allowBlank: true,
    formulae: [`${hiddenSheetName}!$A$1:$A$${keys.length}`]
  });
}

export async function downloadBulkAdjustmentWorkbook(workbook, fileName) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getCellText(cell) {
  if (!cell || cell.value === null || cell.value === undefined) {
    return null;
  }

  if (cell.text != null && String(cell.text).trim() !== "") {
    return String(cell.text).trim();
  }

  if (typeof cell.value === "object" && cell.value.richText) {
    return cell.value.richText
      .map(part => part.text)
      .join("")
      .trim();
  }

  return String(cell.value).trim();
}

function parseAmount(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(
    String(value)
      .replace(/,/g, "")
      .trim()
  );
  return Number.isNaN(parsed) ? null : parsed;
}

function isRowEmpty(values) {
  return values.every(value => value === null || value === "");
}

const BULK_PAYOUT_SHEET = "Bulk Payouts";

export function buildTemplateWorkbook({
  sheetName = BULK_PAYOUT_SHEET,
  headers = [],
  dropdowns = [],
  textColumnIndexes = []
}) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  headers.forEach((header, index) => {
    worksheet.getCell(1, index + 1).value = header;
  });

  const columnsToFormat = textColumnIndexes.length ? textColumnIndexes : headers.map((_, index) => index + 1);

  columnsToFormat.forEach(columnIndex => {
    applyTextFormatToColumn(worksheet, columnIndex, 2, MAX_DATA_ROWS + 1);
  });

  dropdowns.forEach((dropdown, index) => {
    if (!dropdown?.keys?.length || !dropdown.columnIndex) {
      return;
    }
    addListDropdown(
      workbook,
      worksheet,
      dropdown.keys,
      dropdown.columnIndex,
      dropdown.hiddenSheetName || `hidden_list_${index + 1}`
    );
  });

  worksheet.views = [{ state: "frozen", ySplit: 1 }];
  return workbook;
}

export const downloadTemplateWorkbook = downloadBulkAdjustmentWorkbook;

export async function parseTemplateWorkbook(file, { expectedHeaders, sheetName = BULK_PAYOUT_SHEET }) {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const worksheet = workbook.getWorksheet(sheetName) || workbook.worksheets[0];
  if (!worksheet) {
    throw new Error("Excel file is empty.");
  }

  const headerRow = worksheet.getRow(1);
  const actualHeaders = expectedHeaders.map((_, index) => getCellText(headerRow.getCell(index + 1)) || "");

  const headersMatch =
    actualHeaders.length === expectedHeaders.length &&
    expectedHeaders.every((header, index) => header.toLowerCase() === (actualHeaders[index] || "").toLowerCase());

  if (!headersMatch) {
    throw new Error(`Excel headers do not match expected template. Expected: ${expectedHeaders.join(", ")}`);
  }

  const rows = [];
  const lastRow = worksheet.actualRowCount || worksheet.rowCount;

  for (let rowIndex = 2; rowIndex <= lastRow; rowIndex++) {
    const row = worksheet.getRow(rowIndex);
    const cells = {};
    expectedHeaders.forEach((header, index) => {
      cells[header] = getCellText(row.getCell(index + 1));
    });

    if (isRowEmpty(Object.values(cells))) {
      continue;
    }

    rows.push({
      rowNumber: rowIndex,
      cells
    });
  }

  if (!rows.length) {
    throw new Error("No data rows were found in Excel file.");
  }

  return rows;
}

export function parseAmountValue(value) {
  return parseAmount(value);
}
